import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';
import { Shield, Users, Heart, Eye, Target, Award } from 'lucide-react';

export const ChiSiamo = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section - Luxury */}
      <section className="relative min-h-[70vh] flex items-center">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1551884170-09fb70a3a2ed?auto=format&fit=crop&w=2000&q=80" 
            alt="Eye Care Team"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0" style={{background: 'linear-gradient(90deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.8) 50%, rgba(255,255,255,0.3) 100%)'}} />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32">
          <div className="max-w-2xl">
            <p 
              className="text-sm font-medium tracking-[0.3em] uppercase mb-6"
              style={{ color: '#2A9FCB' }}
            >
              La Nostra Storia
            </p>
            <h1 
              className="text-5xl md:text-7xl font-normal mb-8 leading-[1.1] tracking-tight"
              style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#1A1A1A' }}
            >
              Chi Siamo
            </h1>
            <p 
              className="text-lg md:text-xl leading-relaxed"
              style={{ color: '#666666', maxWidth: '500px' }}
            >
              La prevenzione è nostra responsabilità. Un approccio innovativo 
              che mette al centro la persona, non la malattia.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="mb-12">
            <img 
              src="/logo-primevision-small.png" 
              alt="PRIME VISION" 
              className="w-20 h-20 object-contain mx-auto mb-8 opacity-80"
            />
          </div>
          <h2 
            className="text-3xl md:text-4xl font-normal mb-8 leading-tight tracking-tight"
            style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#1A1A1A' }}
          >
            Un'idea semplice ma rivoluzionaria
          </h2>
          <p className="text-xl md:text-2xl leading-relaxed mb-8" style={{ color: '#666666' }}>
            <span className="font-medium" style={{ color: '#1A1A1A' }}>Prime Vision</span> nasce dalla convinzione che 
            le persone non dovrebbero andare dal medico quando stanno male, 
            ma <em>per non ammalarsi mai</em>.
          </p>
          <div className="w-24 h-px bg-[#2A9FCB] mx-auto"></div>
        </div>
      </section>

      {/* Problem & Solution */}
      <section className="py-24 md:py-32 px-6 md:px-12" style={{ background: 'linear-gradient(160deg, #eaf5fc 0%, #f7fbfe 50%, #e1eef8 100%)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p 
                className="text-sm font-medium tracking-[0.3em] uppercase mb-4"
                style={{ color: '#2A9FCB' }}
              >
                Il Problema
              </p>
              <h2 
                className="text-3xl md:text-4xl font-normal mb-8 leading-tight tracking-tight"
                style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#1A1A1A' }}
              >
                La sanità oggi interviene troppo tardi
              </h2>
              <p className="text-lg leading-relaxed mb-6" style={{ color: '#666666' }}>
                Quando la malattia è già presente, quando l'urgenza genera 
                <strong style={{ color: '#1A1A1A' }}> paura, attese e incertezza</strong>.
              </p>
              <p className="text-lg leading-relaxed" style={{ color: '#666666' }}>
                Noi crediamo che la vera cura inizi molto prima. 
                La prevenzione non è un lusso, è un <strong style={{ color: '#1A1A1A' }}>diritto</strong>.
              </p>
            </div>
            <div className="bg-white p-10 md:p-12 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.03)]">
              <p 
                className="text-sm font-medium tracking-[0.3em] uppercase mb-4"
                style={{ color: '#2A9FCB' }}
              >
                La Nostra Risposta
              </p>
              <h3 
                className="text-2xl md:text-3xl font-normal mb-6 leading-tight"
                style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#1A1A1A' }}
              >
                Medicina preventiva, non reattiva
              </h3>
              <p className="text-lg leading-relaxed" style={{ color: '#666666' }}>
                Prime Vision trasforma l'approccio alla salute visiva: 
                seguiamo le persone nel tempo, con percorsi strutturati e programmati, 
                per ridurre l'insorgenza delle patologie e rallentarne la progressione.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p 
              className="text-sm font-medium tracking-[0.3em] uppercase mb-4"
              style={{ color: '#2A9FCB' }}
            >
              I Nostri Valori
            </p>
            <h2 
              className="text-3xl md:text-4xl font-normal mb-6 tracking-tight"
              style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#1A1A1A' }}
            >
              Cosa ci guida ogni giorno
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: Shield, title: 'Prevenzione', desc: 'Controlli regolari e screening per prevenire le patologie prima che si manifestino.' },
              { icon: Heart, title: 'Cura', desc: 'Ogni paziente è unico. Percorsi personalizzati basati sulle reali esigenze.' },
              { icon: Users, title: 'Relazione', desc: 'Costruiamo rapporti di fiducia duraturi, basati su ascolto e rispetto.' },
              { icon: Target, title: 'Precisione', desc: 'Tecnologie all\'avanguardia per diagnosi accurate e trattamenti efficaci.' },
              { icon: Eye, title: 'Competenza', desc: 'Team di specialisti con formazione continua e esperienza consolidata.' },
              { icon: Award, title: 'Eccellenza', desc: 'Standard elevati in ogni aspetto, dalla prima visita al follow-up.' }
            ].map((value, idx) => (
              <div 
                key={idx}
                className="p-8 rounded-2xl bg-white border border-gray-100 hover:border-[#2A9FCB]/30 hover:shadow-[0_8px_30px_rgba(42,159,203,0.1)] transition-all duration-500"
              >
                <value.icon className="w-8 h-8 mb-6" style={{ color: '#2A9FCB' }} />
                <h3 
                  className="text-xl font-medium mb-3"
                  style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#1A1A1A' }}
                >
                  {value.title}
                </h3>
                <p className="text-base leading-relaxed" style={{ color: '#666666' }}>
                  {value.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Patologie */}
      <section className="py-24 md:py-32 px-6 md:px-12" style={{ background: '#2c4667' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-sm font-medium tracking-[0.3em] uppercase mb-4 text-white/70">
              Specializzazioni
            </p>
            <h2 
              className="text-3xl md:text-4xl font-normal mb-6 tracking-tight text-white"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              Patologie che seguiamo
            </h2>
            <p className="text-lg text-white/80 max-w-2xl mx-auto">
              Ci occupiamo di patologie che compromettono la qualità di vita se diagnosticate tardi.
            </p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { name: 'Glaucoma', path: '/specialization/glaucoma' },
              { name: 'Maculopatia', path: '/specialization/maculopathy' },
              { name: 'Cataratta', path: '/specialization/cataract' },
              { name: 'Cheratocono', path: '/specialization/keratoconus' },
              { name: 'Miopia', path: '/specialization/refractive_errors' },
              { name: 'Strabismo', path: '/specialization/strabismus' },
              { name: 'Occhio Secco', path: '/specialization/dry_eye' },
              { name: 'Astigmatismo', path: '/specialization/refractive_errors' },
              { name: 'Presbiopia', path: '/specialization/refractive_errors' },
              { name: 'Calazio', path: '/specialization/chalazion' }
            ].map(condition => (
              <Link 
                key={condition.name} 
                to={condition.path}
                className="px-6 py-3 rounded-full bg-white/20 text-white text-sm font-medium hover:bg-[#2A9FCB] transition-all duration-300"
              >
                {condition.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 
            className="text-3xl md:text-4xl font-normal mb-6 tracking-tight"
            style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#1A1A1A' }}
          >
            Inizia il tuo percorso di prevenzione
          </h2>
          <p className="text-lg mb-10" style={{ color: '#666666' }}>
            Prenota una visita e scopri come possiamo prenderci cura della tua salute visiva.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/booking" 
              className="inline-flex items-center justify-center bg-[#2A9FCB] text-white hover:bg-[#1C729E] rounded-full px-10 py-4 text-sm font-medium transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1"
            >
              Prenota una Visita
            </Link>
            <Link 
              to="/equipe" 
              className="inline-flex items-center justify-center bg-transparent border border-[#2A9FCB] text-[#2A9FCB] hover:bg-[#E6F4FA] rounded-full px-10 py-4 text-sm font-medium transition-all duration-300"
            >
              Conosci l'Équipe
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ChiSiamo;
